import { useClerk } from "@clerk/clerk-react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { Avatar, Button, buttonVariants, SettingRow, Spinner, Text } from "@/shared/ui";
import type { Me } from "../api/getMe";
import { accountDisplay } from "../lib/account";
import { useMe } from "../hooks/useMe";
import { DeleteAccountDialog } from "./DeleteAccountDialog";
import { SignOutIcon, TrashIcon } from "./icons";

const PLAN_DESCRIPTION_KEY: Record<Me["plan_id"], string> = {
  free: "settings.general.planFreeDescription",
  creator: "settings.general.planCreatorDescription",
  power: "settings.general.planPowerDescription",
  studio: "settings.general.planStudioDescription",
  pro: "settings.general.planProDescription",
  max: "settings.general.planMaxDescription",
};

/**
 * Settings → Account. Identity (avatar, name, email), the active plan with an
 * upgrade affordance, and sign-out. Owned by the auth feature and injected into
 * the settings shell by `app`. Ships loading + error states.
 */
export function AccountSection() {
  const { t } = useTranslation();
  const { signOut } = useClerk();
  const { data, isPending, isError, error, refetch } = useMe();
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isPending) {
    return (
      <div className="flex min-h-40 items-center justify-center">
        <Spinner size={22} label={t("common.loading")} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Text variant="body" className="text-ink-soft">
          {t("settings.account.loadError")}
        </Text>
        <Text variant="body" className="text-[13px] text-ink-faint">
          {error.message}
        </Text>
        <Button variant="ghost" size="sm" onClick={() => void refetch()}>
          {t("settings.account.retry")}
        </Button>
      </div>
    );
  }

  const account = accountDisplay(data);

  return (
    <section className="flex flex-col">
      <Text variant="h4" as="h2" className="mb-4">
        {t("settings.tabs.account")}
      </Text>

      <div className="flex items-center gap-3">
        <Avatar kind="user" label={account.initial} className="size-10 text-[15px]" />
        <Text variant="ui" as="div" className="text-[15px]">
          {account.name}
        </Text>
      </div>

      <div className="mt-2 flex flex-col gap-1">
        <SettingRow title={t("settings.account.email")}>
          <Text variant="body" as="span" className="text-ink-soft">
            {data.email}
          </Text>
        </SettingRow>

        <SettingRow title={t("settings.account.plan")} hint={t(PLAN_DESCRIPTION_KEY[account.planId])}>
          {account.planId !== "free" ? (
            <Text variant="ui" as="span">
              {account.planName}
            </Text>
          ) : (
            <Link to="/pricing" className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}>
              {t("common.upgrade")}
            </Link>
          )}
        </SettingRow>

        <SettingRow title={t("settings.account.signOut")} hint={t("settings.account.signOutHint")}>
          <Button variant="ghost" size="sm" onClick={() => void signOut()}>
            <SignOutIcon className="size-4" />
            {t("settings.account.signOut")}
          </Button>
        </SettingRow>

        <SettingRow
          title={t("settings.account.delete")}
          hint={t("settings.account.deleteHint")}
        >
          <Button variant="danger" size="sm" onClick={() => setDeleteOpen(true)}>
            <TrashIcon className="size-4" />
            {t("settings.account.delete")}
          </Button>
        </SettingRow>
      </div>

      <DeleteAccountDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </section>
  );
}
